import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, Pill, Package, ChevronsLeft, ChevronsRight,
  LogOut, Stethoscope, BarChart3,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

/**
 * ReceptionLayout — dockable left rail for receptionist & doctor-as-rec
 * users. Three primary destinations (Home / Reception Desk / Pharmacy)
 * + Inventory (lite-only). Collapsed state is persisted to localStorage
 * so the receptionist's preference survives reloads.
 *
 * The Management link is opt-in per tenant via
 * `capabilities.reception_can_view_management` (see `saas/management.py`
 * `_mgmt_user`). When on, the backend also allows the receptiondesk
 * role to hit /api/management/* — same read-only cockpit as the
 * dedicated management account, no extra login needed.
 */
const NAV = [
  { to: '/reception/home',  label: 'Home',            icon: LayoutDashboard, testid: 'rx-nav-home' },
  { to: '/reception',       label: 'Patient Onboarding', icon: ClipboardList, testid: 'rx-nav-desk' },
  { to: '/pharmacy',        label: 'Pharmacy',        icon: Pill,            testid: 'rx-nav-pharmacy' },
  { to: '/inventory',       label: 'Inventory',       icon: Package,         testid: 'rx-nav-inventory' },
];

export default function ReceptionLayout({ children, pageTitle, pageSubtitle, rightActions }) {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('rec-sidebar-collapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('rec-sidebar-collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed]);
  const branding = tenant?.branding || {};
  const brand = branding.primary_color || '#0f766e';
  const clinic = branding.display_name || tenant?.name || 'Clinic';
  const logo = branding.logo_url;

  const isActive = (to) => location.pathname === to || (to === '/reception' && location.pathname === '/reception');

  // Optionally surface the Management cockpit for receptionists whose
  // tenant has opted-in. Reception-desk users at IIT Hyderabad get this
  // by default; other tenants can flip the capability from Tenant
  // Admin → Capabilities. Also visible unconditionally for
  // management/admin roles that happen to land on this layout.
  const showManagement =
    tenant?.capabilities?.reception_can_view_management === true ||
    ['management', 'admin'].includes((user?.role || '').toLowerCase());

  const nav = showManagement
    ? [...NAV, {
        to: '/management',
        label: 'Management',
        icon: BarChart3,
        testid: 'rx-nav-management',
      }]
    : NAV;

  return (
    <div className="min-h-screen bg-zinc-50 flex" data-testid="reception-layout">
      {/* SIDEBAR */}
      <aside
        className={`flex-shrink-0 bg-white border-r border-zinc-200/70 flex flex-col transition-[width] duration-300 ease-out ${collapsed ? 'w-[68px]' : 'w-[232px]'}`}
        data-testid="rx-sidebar"
        aria-expanded={!collapsed}
      >
        {/* Brand */}
        <div className="h-16 flex items-center px-3 border-b border-zinc-100 gap-2.5">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow-sm overflow-hidden"
               style={{ background: `${brand}15` }}>
            {logo
              ? <img src={logo} alt="logo" className="h-9 w-9 object-contain" />
              : <span className="font-extrabold text-base" style={{ color: brand, fontFamily: 'Manrope' }}>
                  {clinic[0]?.toUpperCase()}
                </span>
            }
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="font-extrabold text-[15px] truncate leading-tight" style={{ color: brand, fontFamily: 'Manrope' }}>
                {clinic}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Reception</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 p-2.5 space-y-1">
          {nav.map(item => {
            const Icon = item.icon;
            const active = isActive(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition group relative ${
                  active
                    ? 'text-white shadow-sm'
                    : 'text-zinc-700 hover:bg-zinc-100'
                }`}
                style={active ? { background: `linear-gradient(135deg, ${brand} 0%, ${brand}cc 100%)` } : {}}
                title={collapsed ? item.label : ''}
              >
                <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-white' : 'text-zinc-500 group-hover:text-zinc-800'}`} />
                {!collapsed && <span className="truncate">{item.label}</span>}
                {active && !collapsed && (
                  <span className="ml-auto h-1.5 w-1.5 rounded-full bg-white/90" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* User chip */}
        <div className="p-2.5 border-t border-zinc-100 space-y-2">
          <div className={`flex items-center gap-2.5 px-2 py-2 rounded-lg ${collapsed ? 'justify-center' : ''}`}>
            <div className="h-8 w-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-700 flex-shrink-0">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-zinc-900 truncate">{user?.name || user?.email}</div>
                <div className="text-[10px] text-zinc-500 capitalize">{user?.role}</div>
              </div>
            )}
          </div>
          <button
            onClick={logout}
            data-testid="rx-logout"
            title={collapsed ? 'Sign out' : ''}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-zinc-600 hover:bg-red-50 hover:text-red-700 transition ${collapsed ? 'justify-center' : ''}`}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
          <button
            onClick={() => setCollapsed(c => !c)}
            data-testid="rx-dock-toggle"
            title={collapsed ? 'Expand' : 'Collapse'}
            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] uppercase tracking-wider font-semibold text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition ${collapsed ? 'justify-center' : ''}`}
          >
            {collapsed
              ? <ChevronsRight className="h-3.5 w-3.5" />
              : <><ChevronsLeft className="h-3.5 w-3.5" /><span>Dock</span></>}
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Page header */}
        <header className="bg-white border-b border-zinc-200/70 h-16 px-7 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-zinc-900 leading-tight truncate" style={{ fontFamily: 'Manrope' }}>
              {pageTitle}
            </h1>
            {pageSubtitle && (
              <p className="text-xs text-zinc-500 mt-0.5 truncate">{pageSubtitle}</p>
            )}
          </div>
          {rightActions && <div className="flex items-center gap-2 flex-shrink-0">{rightActions}</div>}
        </header>
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
